import apiClient from "utils/apiClient";
import { ACTOR_ID_HEADER_KEY, PROFILE_SERVICE_BASE_URL } from "constants";

const API_V1 = `${PROFILE_SERVICE_BASE_URL}/api/v1`;

const unwrap = (response) => response.data;

const withPagination = (params = {}) => {
  const { page = 0, size = 20, ...rest } = params;
  return { page, size, ...rest };
};

const withActor = (actorId) =>
  actorId ? { headers: { [ACTOR_ID_HEADER_KEY]: actorId } } : {};

const normalizeOrganizationPayload = (data) => {
  if (!data || typeof data !== "object") return data;

  const organizationLocationTypeId =
    data.organizationLocationTypeId ||
    data.organizationLocationType?.id ||
    data.organizationLocationType ||
    null;
  const organizationLocationTypeCode = data.organizationLocationType?.code || null;

  return {
    ...data,
    ...(organizationLocationTypeId ? { organizationLocationTypeId } : {}),
    ...(organizationLocationTypeCode ? { organizationLocationTypeCode } : {}),
  };
};

const normalizeOrganizationLocationPayload = (data) => {
  if (!data || typeof data !== "object") return data;

  const organizationLocationTypeId =
    data.organizationLocationTypeId ||
    data.organizationLocationType?.id ||
    data.organizationLocationType ||
    null;
  const organizationLocationTypeCode = data.organizationLocationType?.code || null;

  return {
    ...data,
    ...(organizationLocationTypeId ? { organizationLocationTypeId } : {}),
    ...(organizationLocationTypeCode ? { organizationLocationTypeCode } : {}),
  };
};

export const profileService = {
  organizations: {
    list: async (params = {}) =>
      apiClient
        .get(`${API_V1}/organizations`, { params: withPagination(params) })
        .then(unwrap),

    get: async (organizationId) =>
      apiClient.get(`${API_V1}/organizations/${organizationId}`).then(unwrap),

    getMini: async (organizationId) =>
      apiClient.get(`${API_V1}/organizations/${organizationId}/mini`).then(unwrap),

    getByUser: async (userId, params = {}) =>
      apiClient
        .get(`${API_V1}/organizations/user/${userId}`, { params: withPagination(params) })
        .then(unwrap),

    getMinisByUser: async (userId, params = {}) =>
      apiClient
        .get(`${API_V1}/organizations/user/${userId}/mini`, { params: withPagination(params) })
        .then(unwrap),

    create: async (data, actorId) =>
      apiClient
        .post(
          `${API_V1}/organizations`,
          normalizeOrganizationPayload(data),
          withActor(actorId),
        )
        .then(unwrap),

    update: async (organizationId, data, actorId) =>
      apiClient
        .patch(
          `${API_V1}/organizations/${organizationId}`,
          normalizeOrganizationPayload(data),
          withActor(actorId),
        )
        .then(unwrap),

    updateProperty: async (organizationId, data, actorId) =>
      apiClient
        .patch(
          `${API_V1}/organizations/${organizationId}/props`,
          data,
          withActor(actorId),
        )
        .then(unwrap),

    delete: async (organizationId, actorId) =>
      apiClient.delete(`${API_V1}/organizations/${organizationId}`, withActor(actorId)).then(unwrap),

    users: {
      list: async (organizationId, params = {}) =>
        apiClient
          .get(`${API_V1}/organizations/${organizationId}/users`, {
            params: withPagination(params),
          })
          .then(unwrap),

      add: async (organizationId, data, actorId) =>
        apiClient
          .post(`${API_V1}/organizations/${organizationId}/users`, data, withActor(actorId))
          .then(unwrap),

      update: async (organizationId, userId, data, actorId) =>
        apiClient
          .patch(
            `${API_V1}/organizations/${organizationId}/users/${userId}`,
            data,
            withActor(actorId),
          )
          .then(unwrap),

      remove: async (organizationId, userId, actorId) =>
        apiClient
          .delete(
            `${API_V1}/organizations/${organizationId}/users/${userId}`,
            withActor(actorId),
          )
          .then(unwrap),
    },

    locations: {
      list: async (organizationId, params = {}) =>
        apiClient
          .get(`${API_V1}/organizations/${organizationId}/organization-locations`, {
            params: withPagination(params),
          })
          .then(unwrap),

      get: async (organizationId, organizationLocationId) =>
        apiClient
          .get(
            `${API_V1}/organizations/${organizationId}/organization-locations/${organizationLocationId}`,
          )
          .then(unwrap),

      create: async (organizationId, data, actorId) =>
        apiClient
          .post(
            `${API_V1}/organizations/${organizationId}/organization-locations`,
            normalizeOrganizationLocationPayload(data),
            withActor(actorId),
          )
          .then(unwrap),

      update: async (organizationId, organizationLocationId, data, actorId) =>
        apiClient
          .patch(
            `${API_V1}/organizations/${organizationId}/organization-locations/${organizationLocationId}`,
            normalizeOrganizationLocationPayload(data),
            withActor(actorId),
          )
          .then(unwrap),

      delete: async (organizationId, organizationLocationId, actorId) =>
        apiClient
          .delete(
            `${API_V1}/organizations/${organizationId}/organization-locations/${organizationLocationId}`,
            withActor(actorId),
          )
          .then(unwrap),
    },

    locationTypes: {
      list: async (params = {}) =>
        apiClient
          .get(`${API_V1}/organization-location-types`, {
            params: withPagination({ size: 50, ...params }),
          })
          .then(unwrap),
    },

    services: {
      list: async (params = {}) =>
        apiClient
          .get(`${API_V1}/service-offerings`, { params: withPagination(params) })
          .then(unwrap),

      get: async (serviceId) =>
        apiClient.get(`${API_V1}/service-offerings/${serviceId}`).then(unwrap),

      create: async (data, actorId) =>
        apiClient.post(`${API_V1}/service-offerings`, data, withActor(actorId)).then(unwrap),

      update: async (serviceId, data, actorId) =>
        apiClient
          .patch(`${API_V1}/service-offerings/${serviceId}`, data, withActor(actorId))
          .then(unwrap),

      delete: async (serviceId, actorId) =>
        apiClient.delete(`${API_V1}/service-offerings/${serviceId}`, withActor(actorId)).then(unwrap),

      organizationServices: {
        list: async (organizationId) =>
          apiClient.get(`${API_V1}/service-offerings/organizations/${organizationId}`).then(unwrap),

        add: async (organizationId, serviceId, actorId) =>
          apiClient
            .post(
              `/api/v1/service-offerings/organizations/${organizationId}/${serviceId}`,
              null,
              withActor(actorId),
            )
            .then(unwrap),

        remove: async (organizationId, serviceId, actorId) =>
          apiClient
            .delete(
              `/api/v1/service-offerings/organizations/${organizationId}/${serviceId}`,
              withActor(actorId),
            )
            .then(unwrap),
      },

      organizationLocationServices: {
        list: async (organizationId, organizationLocationId) =>
          apiClient
            .get(
              `/api/v1/service-offerings/organizations/${organizationId}/organization-locations/${organizationLocationId}`,
            )
            .then(unwrap),

        add: async (organizationId, organizationLocationId, serviceId, actorId) =>
          apiClient
            .post(
              `/api/v1/service-offerings/organizations/${organizationId}/organization-locations/${organizationLocationId}/${serviceId}`,
              null,
              withActor(actorId),
            )
            .then(unwrap),

        remove: async (organizationId, organizationLocationId, serviceId, actorId) =>
          apiClient
            .delete(
              `/api/v1/service-offerings/organizations/${organizationId}/organization-locations/${organizationLocationId}/${serviceId}`,
              withActor(actorId),
            )
            .then(unwrap),
      },
    },
  },
};

export default profileService;
